import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/api';
import { ArrowLeft, Save, Settings as SettingsIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [bancoPlazaConfig, setBancoPlazaConfig] = useState({
    cuenta_cobrador: '',
    cuenta_origen: '',
    rif: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const userData = await authService.getMe();
      setUser(userData);
      
      if (userData.banco_plaza_config) {
        setBancoPlazaConfig({
          cuenta_cobrador: userData.banco_plaza_config.cuenta_cobrador || '',
          cuenta_origen: userData.banco_plaza_config.cuenta_origen || '',
          rif: userData.banco_plaza_config.rif || userData.rif || ''
        });
      } else if (userData.rif) {
        setBancoPlazaConfig(prev => ({ ...prev, rif: userData.rif }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/merchant/settings`,
        { banco_plaza_config: bancoPlazaConfig },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setMessage({ type: 'success', text: 'Configuración guardada correctamente' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Error al guardar configuración' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    setBancoPlazaConfig({
      ...bancoPlazaConfig,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b" style={{ backgroundColor: '#051C2C' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 py-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-white hover:text-gray-300"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <img 
              src="/logos/logo-fondo-oscuro.png" 
              alt="Caura Pay" 
              className="h-8"
            />
            <span className="text-xl font-bold text-white border-l border-white/30 pl-4">
              Configuración
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#051C2C' }}>
            Información de la Cuenta
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="text"
                value={user?.email || ''}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
              <input
                type="text"
                value={user?.company_name || ''}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">RIF</label>
              <input
                type="text"
                value={user?.rif || 'No configurado'}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Comisión</label>
              <input
                type="text"
                value={`${user?.fee_percentage || 0}%`}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
              />
            </div>
          </div>
        </div>

        {/* Banco Plaza Configuration */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg" style={{ backgroundColor: '#E8F3FA' }}>
              <SettingsIcon className="w-6 h-6" style={{ color: '#003594' }} />
            </div>
            <h2 className="text-xl font-semibold" style={{ color: '#051C2C' }}>
              Configuración Banco Plaza
            </h2>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              Configura tus cuentas del Banco Plaza para poder procesar pagos y transferencias.
              Esta información es necesaria para usar las funcionalidades de C2P, P2P y transferencias.
            </p>
          </div>

          {message.text && (
            <div className={`mb-6 p-4 rounded-lg ${
              message.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                RIF de la Empresa
              </label>
              <input
                type="text"
                name="rif"
                value={bancoPlazaConfig.rif}
                onChange={handleChange}
                placeholder="J00378944781"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                maxLength={12}
              />
              <p className="text-xs text-gray-500 mt-1">
                12 caracteres incluyendo tipo de persona (J para empresas)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cuenta para Cobros (Cuenta Cobrador)
              </label>
              <input
                type="text"
                name="cuenta_cobrador"
                value={bancoPlazaConfig.cuenta_cobrador}
                onChange={handleChange}
                placeholder="01380011130117777777"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                maxLength={20}
              />
              <p className="text-xs text-gray-500 mt-1">
                Cuenta donde recibirás los pagos (20 dígitos)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cuenta de Origen (Para Transferencias)
              </label>
              <input
                type="text"
                name="cuenta_origen"
                value={bancoPlazaConfig.cuenta_origen}
                onChange={handleChange}
                placeholder="01380011130118888888"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                maxLength={20}
              />
              <p className="text-xs text-gray-500 mt-1">
                Cuenta desde donde realizarás transferencias (20 dígitos)
              </p>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 text-white rounded-lg font-medium transition-all disabled:opacity-50"
              style={{ backgroundColor: '#003594' }}
            >
              <Save className="w-5 h-5" />
              {saving ? 'Guardando...' : 'Guardar Configuración'}
            </button>
          </form>
        </div>

        {/* API Credentials Info */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
          <h3 className="font-semibold text-yellow-900 mb-2">
            📝 Nota sobre credenciales del Banco Plaza
          </h3>
          <p className="text-sm text-yellow-800 mb-2">
            Las credenciales API-KEY y API-SECRET del Banco Plaza están configuradas
            a nivel de sistema en el archivo .env del backend por seguridad.
          </p>
          <p className="text-sm text-yellow-800">
            Si necesitas usar credenciales diferentes, contacta al administrador del sistema.
          </p>
        </div>
      </div>
    </div>
  );
}
